import type { AnalyticsEventName } from "@/lib/analytics/events";

export type PageViewEvent = {
  name: AnalyticsEventName;
  props: Record<string, string | number | boolean | null | undefined>;
};

import { localeFromPathname, stripLocalePrefix } from "@/lib/i18n";

function normalizeLocalizedPath(pathname: string): { path: string; locale: string } {
  return { path: stripLocalePrefix(pathname), locale: localeFromPathname(pathname) };
}

export function pageViewEventFor(pathname: string): PageViewEvent | null {
  const normalized = normalizeLocalizedPath(pathname);
  pathname = normalized.path;
  const localeProps = { locale: normalized.locale };
  if (pathname === "/") return { name: "view_home", props: { page_type: "home", ...localeProps } };
  if (pathname === "/ticket-alerts") return { name: "view_ticket_alerts", props: { page_type: "ticket_alerts", ...localeProps } };
  if (pathname === "/restock-alerts") return { name: "view_restock_alerts", props: { page_type: "restock_alerts", ...localeProps } };
  if (pathname === "/availability") return { name: "view_availability", props: { page_type: "availability", ...localeProps } };
  if (pathname === "/availability-monitor") return { name: "view_availability_monitor", props: { page_type: "availability_monitor", ...localeProps } };
  if (pathname === "/pricing") return { name: "view_pricing", props: { page_type: "pricing", ...localeProps } };
  if (pathname === "/contact") return { name: "view_contact", props: { page_type: "contact", ...localeProps } };
  if (pathname === "/trust") return { name: "view_trust", props: { page_type: "trust", ...localeProps } };
  if (pathname === "/status") return { name: "view_status", props: { page_type: "status", ...localeProps } };
  if (pathname === "/blog") return { name: "view_blog", props: { page_type: "blog_index", ...localeProps } };
  if (pathname.startsWith("/blog/")) {
    const slug = pathname.split("/").filter(Boolean).slice(-1)[0] ?? null;
    return { name: "view_blog_post", props: { page_type: "blog_post", slug, ...localeProps } };
  }
  if (pathname === "/routes") return { name: "view_routes", props: { page_type: "routes_index", ...localeProps } };
  if (pathname.startsWith("/routes/")) {
    const routeSlug = pathname.split("/").filter(Boolean).slice(-1)[0] ?? null;
    return { name: "view_route_detail", props: { page_type: "route_detail", route_slug: routeSlug, ...localeProps } };
  }
  if (pathname === "/sold-out") return { name: "view_sold_out", props: { page_type: "sold_out", ...localeProps } };
  if (pathname === "/last-minute") return { name: "view_last_minute", props: { page_type: "last_minute", ...localeProps } };
  if (pathname === "/how-it-works") return { name: "view_how_it_works", props: { page_type: "how_it_works", ...localeProps } };
  if (pathname === "/faq") return { name: "view_faq", props: { page_type: "faq", ...localeProps } };
  if (pathname === "/privacy") return { name: "view_privacy", props: { page_type: "privacy", ...localeProps } };
  if (pathname === "/terms") return { name: "view_terms", props: { page_type: "terms", ...localeProps } };
  return null;
}

export function isPublicAnalyticsPath(pathname: string): boolean {
  return pageViewEventFor(pathname) !== null;
}
